namespace SaStylingUtils {
    // #region Class Names

    export const DisplayNoneClassName = "d-none";
    export const ShowClassName = "show";

    // #endregion

    // #region Screen Width and Breakpoints

    /**
     * Each breakpoint is defined as the minimum width for that breakpoint, so for example Small applies at widths of 576px and above, Medium applies at widths of 768px and above, etc. This matches the way Bootstrap defines its breakpoints, and can be used in conjunction with CSS media queries that use the same breakpoint definitions.
     */
    export enum Breakpoints {
        ExtraSmall = 0,
        Small = 576,
        Medium = 768,
        Large = 992,
        ExtraLarge = 1200,
        ExtraExtraLarge = 1400,
    }

    /**
     * Defined as "Not Desktop", true for mobile or tablet screen sizes.
     * Could also be characterized as "Mostly Vertical Layout".
     */
    export function isMobile(): boolean {
        return window.innerWidth < Breakpoints.Large;
    }

    // #endregion

    // #region Visibility

    export function elementIsShown(
        element: Element | null | undefined,
    ): boolean {
        if (!element) return false;

        return element.classList.contains(ShowClassName);
    }

    export function elementIsVisible(
        element: Element | null | undefined,
    ): boolean {
        if (!element) return false;

        const style = globalThis.getComputedStyle(element);
        return (
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            (element as HTMLElement).offsetHeight > 0
        );
    }

    export function elementIsVisibleAndNotDisabled(
        btn: HTMLButtonElement,
    ): boolean {
        // Check the "data-available" attribute, used in some cases, but fall back to true if not specified
        const attr = (btn.dataset?.available || "").toLowerCase();

        return elementIsVisible(btn) && !btn.disabled && attr !== "false";
    }

    export function isElementScrolledIntoView(elem: HTMLElement): boolean {
        const docViewTop = $(globalThis).scrollTop() ?? 0;
        const docViewBottom = docViewTop + ($(globalThis).height() ?? 0);
        const elemTop = $(elem).offset()?.top ?? 0;
        const elemBottom = elemTop + ($(elem).height() ?? 0);

        return elemBottom <= docViewBottom && elemTop >= docViewTop;
    }

    export function scrollToHeading(
        heading: Element,
        targetId: string,
        headerOffset: number = 130,
    ) {
        // Use manual scroll position to avoid html scroll-padding: 280px being too generous.
        // Target offset accounts for the fixed global nav + sticky jump nav bar (~130px).
        const elementTop =
            heading.getBoundingClientRect().top + globalThis.scrollY;
        globalThis.scrollTo({
            top: elementTop - headerOffset,
            behavior: "instant",
        });
        history.pushState(null, "", targetId);
    }
    // #endregion

    // #region Bootstrap Collapse

    const bootstrapNotLoadedErrorMessage =
        "Bootstrap's JavaScript is required for this functionality, but it was not found. Please ensure Bootstrap's JavaScript is included in the page.";

    /** Finds a collapse element by its ID. */
    export function bsCollapseFind(collapseId: string): HTMLElement | null {
        return document.getElementById(collapseId);
    }

    /** Collapses have a separate header element that can be clicked to toggle the collapse state, most typically found in Accordions. This finds it. */
    export function bsCollapseFindAccordionHeader<
        TElement extends HTMLElement = HTMLButtonElement,
    >(collapseId: string): TElement | null {
        return document.querySelector(
            `button.accordion-button[data-bs-toggle="collapse"][data-bs-target="#${collapseId}"]`,
        );
    }

    /** Clicks the header element for a collapse, if it exists and is not disabled. */
    function bsCollapseClickHeader(collapseId: string): void {
        const btn = bsCollapseFindAccordionHeader(collapseId);
        if (btn && !btn.disabled) btn.click();
    }

    /** Shows a collapse element by its ID. */
    export function bsCollapseShow(collapseId: string): boolean {
        const collapseEl = bsCollapseFind(collapseId);

        if (!collapseEl || SaStylingUtils.elementIsShown(collapseEl))
            return false;

        if (globalThis.bootstrap?.Collapse) {
            bootstrap.Collapse.getOrCreateInstance(collapseEl, {
                toggle: false,
            }).show();
            return true;
        }

        console.error(bootstrapNotLoadedErrorMessage);
        bsCollapseClickHeader(collapseId);
        return true;
    }

    /** Hides a collapse element by its ID. */
    export function bsCollapseHide(collapseId: string): boolean {
        const collapseEl = bsCollapseFind(collapseId);

        if (!collapseEl || !SaStylingUtils.elementIsShown(collapseEl))
            return false;

        if (globalThis.bootstrap?.Collapse) {
            bootstrap.Collapse.getOrCreateInstance(collapseEl, {
                toggle: false,
            }).hide();
            return true;
        }

        console.error(bootstrapNotLoadedErrorMessage);
        bsCollapseClickHeader(collapseId);
        return true;
    }

    // #endregion

    // #region Bootstrap Modal

    export function bsModalShow(modalId: string): boolean {
        const modalEl = document.getElementById(modalId);

        if (!modalEl) return false;

        if (globalThis.bootstrap?.Modal) {
            bootstrap.Modal.getOrCreateInstance(modalEl).show();
            return true;
        }

        console.error(bootstrapNotLoadedErrorMessage);
        return false;
    }

    export function bsModalHide(modalId: string): boolean {
        const modalEl = document.getElementById(modalId);

        if (!modalEl) return false;

        if (globalThis.bootstrap?.Modal) {
            bootstrap.Modal.getOrCreateInstance(modalEl).hide();
            return true;
        }

        console.error(bootstrapNotLoadedErrorMessage);
        return false;
    }

    // #endregion

    // #region Bootstrap Popover

    const popoverClickHandlers = new WeakMap<
        HTMLElement,
        (event: MouseEvent) => void
    >();
    let popoverDismissListenersInitialized = false;

    /** Hides managed popovers unless the target is inside their trigger or content. */
    export function bsPopoverHideOpen(eventTarget?: EventTarget | null): void {
        if (!globalThis.bootstrap?.Popover) {
            return;
        }

        document
            .querySelectorAll<HTMLElement>("[aria-describedby]")
            .forEach((element) => {
                if (!popoverClickHandlers.has(element)) {
                    return;
                }

                const instance = bootstrap.Popover.getInstance(element);
                if (!instance) {
                    return;
                }

                const describedById = element.getAttribute("aria-describedby");
                const popoverElement = describedById
                    ? document.getElementById(describedById)
                    : null;
                const targetNode = eventTarget as Node | null;
                const clickedInsideTrigger = targetNode
                    ? element.contains(targetNode)
                    : false;
                const clickedInsidePopover = targetNode
                    ? (popoverElement?.contains(targetNode) ?? false)
                    : false;

                if (!clickedInsideTrigger && !clickedInsidePopover) {
                    instance.hide();
                    element.blur();
                }
            });
    }

    /** Initializes managed popovers and registers them for outside-click and scroll dismissal via bsPopoverHideOpen. */
    export function bsPopoverInitialize(
        $container: JQuery,
        triggerSelector: string,
    ): void {
        if (!globalThis.bootstrap?.Popover) {
            console.error(bootstrapNotLoadedErrorMessage);
            return;
        }

        $container.find(triggerSelector).each(function (_, element) {
            const previousHandler = popoverClickHandlers.get(element);
            if (previousHandler) {
                element.removeEventListener("click", previousHandler);
            }
            bootstrap.Popover.getInstance(element)?.dispose();

            const popover = new bootstrap.Popover(element, {
                trigger: "manual",
                html: true,
                container: "body",
            });

            const onClick = (event: MouseEvent): void => {
                event.preventDefault();
                event.stopPropagation();

                if (element.getAttribute("aria-describedby")) {
                    popover.hide();
                    element.blur();
                } else {
                    popover.show();
                }
            };
            popoverClickHandlers.set(element, onClick);
            element.addEventListener("click", onClick);
        });

        if (popoverDismissListenersInitialized) {
            return;
        }

        popoverDismissListenersInitialized = true;
        document.addEventListener("click", (event) => {
            bsPopoverHideOpen(event.target);
        });
        globalThis.addEventListener("scroll", () => bsPopoverHideOpen(), {
            passive: true,
        });
    }

    // #endregion

    // #region Bootstrap Tooltip

    export function initializeTooltips(
        scope: Document | Element = document,
    ): void {
        if (!globalThis.bootstrap?.Tooltip) {
            return;
        }

        scope
            .querySelectorAll('[data-bs-toggle="tooltip"]')
            .forEach(function (tooltipTriggerEl) {
                globalThis.bootstrap.Tooltip.getOrCreateInstance(
                    tooltipTriggerEl,
                );
            });
    }

    // #endregion

    // #region Main Navigation Helpers

    export function getVisibleStickyNav() {
        const desktopNav = document.querySelector(".saNavBarDesktop");
        const mobileNav = document.querySelector(".saNavBarMobile");

        if (isMobile() && elementIsVisible(mobileNav)) return mobileNav;
        if (!isMobile() && elementIsVisible(desktopNav)) return desktopNav;

        if (elementIsVisible(mobileNav)) return mobileNav;
        if (elementIsVisible(desktopNav)) return desktopNav;

        return null;
    }

    export function setStickyNavCollapsedState(isCollapsed: boolean): void {
        document.body.classList.toggle("has-collapsed-course-nav", isCollapsed);

        const stickyNav = getVisibleStickyNav();
        if (stickyNav) {
            stickyNav.classList.toggle("is-collapsed", isCollapsed);
        }
    }

    export function getStickyNavBottomInPx(): number {
        const stickyNav = getVisibleStickyNav();

        if (!stickyNav) {
            return 0;
        }

        const wasCollapsed = stickyNav.classList.contains("is-collapsed");

        if (wasCollapsed) {
            // Expand it temporarily
            stickyNav.classList.remove("is-collapsed");
        }

        const rect = stickyNav.getBoundingClientRect();
        const result = rect.bottom;

        if (wasCollapsed) {
            // Collapse it back
            stickyNav.classList.add("is-collapsed");
        }

        return result;
    }

    // #endregion
}

globalThis.SaStylingUtils = SaStylingUtils;
